import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

import VoiceChat from "./VoiceChat";
import Navbar from "./Navbar";
import Footer from "./Footer";
import MaintenanceModal from "./MaintenanceModal";
import { useDispatch, useSelector } from "react-redux";
import LoadingScreen from "./LoadingScreen";
import { callBackend } from "../backend/callBackend";
import { initiatePayUCheckout, submitPayUForm } from "../backend/paymentService";
import { checkAndApplyCoupon } from "../backend/couponService";
import { setConfig } from "../redux/configSlice";
import { Link, useLocation } from "react-router-dom";
import {
  resolvePriceFromMap,
  getRoomLimits,
  getMaxRoomsForType,
  resolvePerPersonPrice,
  calculateBookingTotal,
  resolveAdvanceBookingAmount,
  isRoomTypeMatch,
  normalizeRoomType,
  getRoomCardDetails,
} from "../utils/priceHelper";
import "../styles/BookingPage.css";

import {
  FaCheckCircle,
  FaShieldAlt,
  FaCalendarAlt,
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaBed,
  FaArrowLeft,
  FaStar,
  FaMapMarkerAlt,
  FaTag,
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
  FaWhatsapp,
  FaCreditCard,
  FaLock,
} from "react-icons/fa";


const ROOM_DETAILS = {
  "Standard AC Room": {
    price: 2500,
    maxGuests: 2,
    bed: "1 King Bed",
    features: "Air Conditioned, Attached Bath, Garden View",
    tag: "Most Popular",
  },
  "Standard Non-AC Room": {
    price: 2000,
    maxGuests: 2,
    bed: "1 King Bed",
    features: "Ceiling Fan, Attached Bath, Airy",
    tag: "Budget Choice",
  },
  "Standard Ac Room": {
    price: 2500,
    maxGuests: 2,
    bed: "1 King Bed",
    features: "Air Conditioned, Attached Bath, Garden View",
    tag: "Most Popular",
  },
  "Standard Non-Ac Room": {
    price: 2000,
    maxGuests: 2,
    bed: "1 King Bed",
    features: "Ceiling Fan, Attached Bath, Airy",
    tag: "Budget Choice",
  },
  "Double AC Room": {
    price: 4800,
    maxGuests: 4,
    bed: "2 Double Beds",
    features: "Air Conditioned, Balcony, Spacious Family Suite",
    tag: "Family Favorite",
  },
  "Double Non-AC Room": {
    price: 4000,
    maxGuests: 4,
    bed: "2 Double Beds",
    features: "Large Family Room, Natural Breeze",
    tag: "Spacious",
  },
  "Double Ac Room": {
    price: 4800,
    maxGuests: 4,
    bed: "2 Double Beds",
    features: "Air Conditioned, Balcony, Spacious Family Suite",
    tag: "Family Favorite",
  },
  "Double Non-Ac Room": {
    price: 4000,
    maxGuests: 4,
    bed: "2 Double Beds",
    features: "Large Family Room, Natural Breeze",
    tag: "Spacious",
  },
};

const BookingForm = () => {
  const location = useLocation();
  const todayStr = new Date().toISOString().split("T")[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    people: 2,
    child: 0,
    totalAmount: "",
    paidAmount: "",
    email: "",
  });

  const [checkinDate, setCheckinDate] = useState(todayStr);
  const [checkoutDate, setCheckoutDate] = useState(tomorrowStr);
  const [roomCount, setRoomCount] = useState(1);
  const [totalAmount, setTotalAmount] = useState("");
  const [bookingAmount, setBookingAmount] = useState(1000);
  const [roomTypes, setRoomTypes] = useState([
    "Standard AC Room",
    "Standard Non-AC Room",
    "Double AC Room",
    "Double Non-AC Room",
  ]);
  const [selectedRoomType, setSelectedRoomType] = useState("Standard AC Room");
  const [isLoading, setIsLoading] = useState(false);
  const [roomPrices, setRoomPrices] = useState({});
  const [urlParamsChecked, setUrlParamsChecked] = useState(false);
  const [useVoiceMode, setUseVoiceMode] = useState(false);
  const [isAiEnabled, setIsAiEnabled] = useState(false);
  const [availError, setAvailError] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponSuccess, setCouponSuccess] = useState("");

  // Booking Success Confirmation Modal state
  const [bookingSuccessData, setBookingSuccessData] = useState(null);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [isBackendOffline, setIsBackendOffline] = useState(false);

  const hotel = useSelector((state) => state.hotel.data);

  const configData = useSelector((state) => state.config?.data);
  const loadRef = useRef(false);
  const dispatch = useDispatch();

  // Read URL Params (from homepage search widget or external campaign links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCheckin = params.get("checkin");
    const urlCheckout = params.get("checkout");
    const urlRoom = params.get("room");
    const urlGuests = params.get("guests");
    const urlChild = params.get("child");

    if (urlCheckin) setCheckinDate(urlCheckin);
    if (urlCheckout) setCheckoutDate(urlCheckout);
    if (urlRoom) setSelectedRoomType((curr) => normalizeRoomType(urlRoom, roomTypes));
    if (urlGuests && !isNaN(parseInt(urlGuests))) {
      setFormData((prev) => ({ ...prev, people: parseInt(urlGuests) }));
    }
    if (urlChild && !isNaN(parseInt(urlChild))) {
      setFormData((prev) => ({ ...prev, child: parseInt(urlChild) }));
    }

    setUrlParamsChecked(true);
  }, [location.search, roomTypes]);

  // Sync state from Redux configData
  useEffect(() => {
    if (configData) {
      if (configData.total_room_types && configData.total_room_types.length > 0) {
        setRoomTypes(configData.total_room_types);
        setSelectedRoomType((curr) => normalizeRoomType(curr, configData.total_room_types));
      }
      if (configData.price_map) setRoomPrices(configData.price_map);
      if (configData.isAiEnabled !== undefined) setIsAiEnabled(configData.isAiEnabled);
    }
  }, [configData]);

  // Fetch Config only if missing
  useEffect(() => {
    if (configData || loadRef.current) return;
    loadRef.current = true;

    callBackend("backendBookingService", "fetchConfig", {})
      .then((res) => {
        if (res?.data) {
          dispatch(setConfig(res.data));
          setIsBackendOffline(false);
        }
      })
      .catch((err) => {
        console.warn("⚠️ Backend service unreachable on initial load:", err);
        setIsBackendOffline(true);
      });
  }, [configData, dispatch]);


  // Calculate Duration in nights
  const calculateDays = () => {
    const inDate = checkinDate ? new Date(checkinDate) : null;
    const outDate = checkoutDate ? new Date(checkoutDate) : null;

    if (
      inDate instanceof Date &&
      outDate instanceof Date &&
      !isNaN(inDate) &&
      !isNaN(outDate) &&
      outDate > inDate
    ) {
      const diffInMs = outDate - inDate;
      return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    }
    return 1;
  };

  const dayCount = calculateDays();

  const DOUBLE_ROOM_PAIR = [4, 5];
  const isDoubleRoomType = (type) =>
    String(type || "").toLowerCase().includes("double");

  // Dynamic price helper from backend config API with semantic resolution
  const getRoomPrice = (type) => {
    return resolvePriceFromMap(configData?.price_map || roomPrices, type, 2500);
  };

  // Dynamic Availability State
  const [availableCount, setAvailableCount] = useState(null);
  const [rawAvailData, setRawAvailData] = useState(null);
  const [isCheckingAvail, setIsCheckingAvail] = useState(false);

  // Automatically check availability on initial load and whenever dates or room type changes
  useEffect(() => {
    let isMounted = true;
    if (!checkinDate || !checkoutDate) return;
    const inD = new Date(checkinDate);
    const outD = new Date(checkoutDate);
    if (isNaN(inD.getTime()) || isNaN(outD.getTime()) || outD <= inD) return;

    setIsCheckingAvail(true);
    setAvailError(null);

    const isDouble = isDoubleRoomType(selectedRoomType);

    callBackend("backendBookingService", "checkAvailability", {
      checkinDate,
      checkoutDate,
      roomType: selectedRoomType,
      roomCount: isDouble ? 1 : 1,
    })
      .then((res) => {
        if (!isMounted) return;
        setIsCheckingAvail(false);
        const data = res?.data || {};
        setRawAvailData(data);
        if (isDouble) {
          const count =
            !data.isDoublePairBooked &&
              Array.isArray(data.availableRooms) &&
              data.availableRooms.includes(4) &&
              data.availableRooms.includes(5)
              ? 1
              : 0;
          setAvailableCount(count);
          if (count === 0) {
            setAvailError(
              "Double Room is fully booked for selected dates. Please choose a Standard Room or alternate dates."
            );
          }
        } else {
          const availRooms = data.availableRooms || [];
          const count =
            typeof data.availableRoomCount === "number"
              ? data.availableRoomCount
              : availRooms.length;
          setAvailableCount(count);
          if (count === 0) {
            setAvailError(
              `Rooms are fully booked for ${checkinDate} to ${checkoutDate}. Please select alternate dates.`
            );
          }
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setIsCheckingAvail(false);
        console.warn("Availability check warning:", err);
      });

    return () => {
      isMounted = false;
    };
  }, [checkinDate, checkoutDate, selectedRoomType]);

  const configMaxRooms = getMaxRoomsForType(configData, selectedRoomType);
  const maxSelectableRooms =
    availableCount !== null ? Math.min(configMaxRooms, availableCount) : configMaxRooms;

  // Auto-clamp room count if it exceeds allowed or available rooms
  useEffect(() => {
    if (maxSelectableRooms > 0 && roomCount > maxSelectableRooms) {
      setRoomCount(maxSelectableRooms);
    } else if (maxSelectableRooms === 0) {
      setRoomCount(0);
    } else if (maxSelectableRooms > 0 && roomCount === 0) {
      setRoomCount(1);
    }
  }, [maxSelectableRooms, roomCount]);

  const roomLimits = getRoomLimits(configData, selectedRoomType);
  const maxAllowedGuests = roomLimits.maxLimit * Math.max(1, roomCount);
  const noCostCapacity = roomLimits.noCostLimit * Math.max(1, roomCount);

  const bookingCalc = calculateBookingTotal({
    configData,
    roomIdentifier: selectedRoomType,
    roomCount,
    guestCount: formData.people,
    nights: dayCount,
    roomPricesOverride: roomPrices,
  });

  // Recalculate price & token advance from configData
  useEffect(() => {
    if (!urlParamsChecked) return;

    const baseAdvancePerRoom = resolveAdvanceBookingAmount(configData, 1000);

    // Prices always come from config; the server re-checks them in createBooking
    setBookingAmount(baseAdvancePerRoom * roomCount * dayCount);

    const calc = calculateBookingTotal({
      configData,
      roomIdentifier: selectedRoomType,
      roomCount,
      guestCount: formData.people,
      nights: dayCount,
      roomPricesOverride: roomPrices,
    });
    const discount = appliedCoupon?.discountAmount || 0;
    setTotalAmount(Math.max(0, calc.totalAmount - discount));
  }, [
    roomCount,
    selectedRoomType,
    formData.people,
    checkinDate,
    checkoutDate,
    urlParamsChecked,
    dayCount,
    roomPrices,
    configData,
    appliedCoupon,
  ]);

  // Handle Apply Coupon Code via backend API
  const handleApplyCoupon = async (e) => {
    if (e) e.preventDefault();
    const trimmed = couponCode.trim().toUpperCase();
    if (!trimmed) {
      setCouponError("Please enter a coupon code.");
      setCouponSuccess("");
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError("");
    setCouponSuccess("");

    try {
      const response = await checkAndApplyCoupon({
        couponCode: trimmed,
        totalAmount: bookingCalc.totalAmount,
        roomType: selectedRoomType,
        roomCount,
        nights: dayCount,
        phone: formData.phone,
        email: formData.email,
        checkinDate,
        checkoutDate,
      });

      // Backend replies { success, data, message }; the reason (e.g. "enter your mobile number")
      // lives in the top-level message, not inside data
      const resData = {
        ...(response?.data || {}),
        success: response?.success,
        message: response?.message || response?.data?.message,
      };
      const discount = Number(resData.discountAmount ?? resData.discount ?? 0);

      if (resData.success !== false && discount > 0) {
        setAppliedCoupon({
          code: trimmed,
          discountAmount: discount,
          message:
            resData.message ||
            `Coupon "${trimmed}" applied! You saved ₹${discount.toLocaleString("en-IN")}`,
          ...resData,
        });
        setCouponSuccess(
          resData.message ||
          `Coupon "${trimmed}" applied! You saved ₹${discount.toLocaleString("en-IN")}`
        );
        setCouponError("");
      } else {
        setAppliedCoupon(null);
        setCouponError(
          resData.message ||
          resData.error ||
          `Invalid or expired coupon code "${trimmed}".`
        );
        setCouponSuccess("");
      }
    } catch (err) {
      console.error("Failed to apply coupon:", err);
      setAppliedCoupon(null);
      setCouponError("Failed to apply coupon. Please check the code and try again.");
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
    setCouponSuccess("");
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "people" || id === "child") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 2) return;
    }
    if (id === "phone") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
      if (appliedCoupon) {
        setAppliedCoupon(null);
        setCouponSuccess("");
        setCouponError("Mobile number updated. Please re-apply your coupon code.");
      }
    }
    if (id === "email") {
      if (/\s/.test(value)) return;
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const validateForm = () => {
    if (!formData.fullName.trim()) {
      alert("Please enter your Full Name.");
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      alert("Please enter a valid 10-digit Phone Number.");
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      alert("Please enter a valid Email Address.");
      return false;
    }
    if (!formData.people || formData.people <= 0) {
      alert("Please enter number of guests.");
      return false;
    }
    if (formData.people > maxAllowedGuests) {
      alert(
        `For ${roomCount} ${selectedRoomType}, maximum allowed capacity is ${maxAllowedGuests} guests (${roomLimits.maxLimit} guests/room). Please adjust the number of guests or select more rooms.`
      );
      return false;
    }
    if (!selectedRoomType) {
      alert("Please select a Room Type.");
      return false;
    }
    if (!checkinDate || !checkoutDate) {
      alert("Please select both Check-In and Check-Out dates.");
      return false;
    }
    if (maxSelectableRooms === 0 || roomCount <= 0) {
      alert("The selected room type is sold out for these dates. Please select alternate dates or choose another room type.");
      return false;
    }
    return true;
  };

  const sendBookingNotification = async (booking) => {
    if (process.env.REACT_APP_ENABLE_EMAIL_NOTIFICATION !== "true") {
      console.log(
        "📧 [EMAIL_DISABLED] Email notifications are disabled in test mode. (Set REACT_APP_ENABLE_EMAIL_NOTIFICATION=true to enable)"
      );
      return;
    }

    try {
      const chkDate = booking.checkinDate?._seconds
        ? new Date(booking.checkinDate._seconds * 1000)
        : new Date(booking.checkinDate);
      const emailPayload = {
        name: booking.name,
        email: booking.email,
        roomType: booking.roomType,
        people: booking.people,
        phone: booking.phone,
        bookingDate: chkDate.toLocaleDateString(),
        isSendBookingInfoEmail: true,
      };
      await callBackend("backendBookingService", "sendInfoEmail", emailPayload);
    } catch (error) {
      console.error("Failed to send booking notification:", error);
    }
  };

  const fetchBookingAvailability = async (payload) => {
    const response = await callBackend(
      "backendBookingService",
      "checkAvailability",
      payload
    );
    if (!response || !response.data) {
      throw new Error("Availability service is currently unreachable");
    }
    return response.data;
  };

  const createBooking = async (payload) => {
    const response = await callBackend(
      "backendBookingService",
      "createBooking",
      payload
    );
    if (!response || response.success === false) {
      const err = new Error(response?.error || "Failed to create booking");
      err.isServiceDown = false;
      throw err;
    }
    return response.data;
  };

  const selectRooms = (availableRooms, requiredCount, roomType) => {
    if (isDoubleRoomType(roomType)) {
      return [...DOUBLE_ROOM_PAIR];
    }
    if (!availableRooms || availableRooms.length === 0) return [];
    const numRooms = availableRooms.map(Number);
    // Prioritize standard rooms before using room 4 or 5
    const standardOnly = numRooms.filter((r) => !DOUBLE_ROOM_PAIR.includes(r));
    const doublePairRooms = numRooms.filter((r) => DOUBLE_ROOM_PAIR.includes(r));
    const prioritized = [...standardOnly, ...doublePairRooms];
    return prioritized.slice(0, requiredCount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setAvailError(null);

    try {
      const response = await fetchBookingAvailability({
        checkinDate,
        checkoutDate,
        roomCount: isDoubleRoomType(selectedRoomType) ? 1 : roomCount,
        roomType: selectedRoomType,
      });

      if (!response.canBook) {
        const remaining = (response.maxRoomLimit || 7) - (response.alreadyBookedRooms || 0);
        setAvailError(
          remaining > 0
            ? `Rooms are limited for selected dates. Only ${remaining} room(s) remaining.`
            : `Rooms are currently fully booked for ${checkinDate} to ${checkoutDate}. Please select alternate dates.`
        );
        setIsLoading(false);
        return;
      }

      const selectedRooms = selectRooms(
        response.availableRooms,
        roomCount,
        selectedRoomType
      );
      const bookingId = `GHH-${Math.floor(100000 + Math.random() * 900000)}`;

      const effectiveTotalAmount = Math.max(0, Number(totalAmount) || 0);
      const effectiveBookingAmount = Math.max(
        1,
        Math.min(
          Number(bookingAmount) || 1000,
          effectiveTotalAmount > 0 ? effectiveTotalAmount : Number(bookingAmount) || 1000
        )
      );

      // Compute guest category: 2 guests with 0 children -> Couple, else Family
      const isCouple = Number(formData.people) === 2 && (!formData.child || Number(formData.child) === 0);
      const computedGuestCategory = isCouple ? "Couple" : "Family";

      // 1. Build Callback URLs for PayU return
      const surl = `${window.location.origin}/booking-confirmation?status=success&bookingId=${bookingId}&amount=${effectiveBookingAmount}&totalAmount=${effectiveTotalAmount}&name=${encodeURIComponent(formData.fullName)}&roomType=${encodeURIComponent(selectedRoomType)}&roomCount=${roomCount}&people=${formData.people}&child=${formData.child || 0}&guestCategory=${encodeURIComponent(computedGuestCategory)}&checkinDate=${checkinDate}&checkoutDate=${checkoutDate}`;
      const furl = `${window.location.origin}/booking-confirmation?status=failure&bookingId=${bookingId}&amount=${effectiveBookingAmount}&totalAmount=${effectiveTotalAmount}&name=${encodeURIComponent(formData.fullName)}&roomType=${encodeURIComponent(selectedRoomType)}&roomCount=${roomCount}&people=${formData.people}&child=${formData.child || 0}&guestCategory=${encodeURIComponent(computedGuestCategory)}&checkinDate=${checkinDate}&checkoutDate=${checkoutDate}`;

      // 2. Initiate payment with Payment Microservice first
      const payuResponse = await initiatePayUCheckout({
        appSource: "GHH",
        entityId: bookingId,
        amount: effectiveBookingAmount,
        productInfo: `${selectedRoomType} (${roomCount} Room) - GHH Booking`,
        customer: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        },
        surl,
        furl,
        metadata: {
          bookingId,
          roomType: selectedRoomType,
          roomCount: isDoubleRoomType(selectedRoomType) ? 1 : roomCount,
          people: Number(formData.people),
          child: Number(formData.child || 0),
          guestCategory: computedGuestCategory,
          checkinDate,
          checkoutDate,
          totalAmount: effectiveTotalAmount,
          bookingAmount: effectiveBookingAmount,
        },
      });

      // 3. Create booking record in database only after payment is initiated successfully
      const bookingData = {
        bookingId,
        txnId: payuResponse?.txnid || payuResponse?.txnId || "",
        name: formData.fullName,
        phone: formData.phone,
        people: Number(formData.people),
        child: Number(formData.child || 0),
        guestCategory: computedGuestCategory,
        email: formData.email,
        checkinDate: new Date(checkinDate).toISOString(),
        checkoutDate: new Date(checkoutDate).toISOString(),
        roomCount: isDoubleRoomType(selectedRoomType) ? 1 : selectedRooms.length,
        roomNumber: selectedRooms,
        roomType: selectedRoomType,
        isBookingNotConfirm: true,
        paymentStatus: "PENDING",
        totalAmount: effectiveTotalAmount,
        bookingAmount: effectiveBookingAmount,
        remainingAmount: Math.max(0, effectiveTotalAmount - effectiveBookingAmount),
        couponCode: appliedCoupon ? appliedCoupon.code : "",
        discountAmount: appliedCoupon ? appliedCoupon.discountAmount : 0,
        appliedCoupon: appliedCoupon || null,
        createdAt: new Date().toISOString(),
      };

      await createBooking(bookingData);

      // 5. Submit hidden form and redirect guest to PayU Hosted Checkout
      submitPayUForm(payuResponse);
    } catch (error) {
      console.error("Error initiating PayU booking payment:", error);
      if (error.isServiceDown) {
        setShowMaintenanceModal(true);
      } else {
        setAvailError(
          error.message ||
          "Could not process booking. Please check your details or select alternate dates."
        );
      }
      setIsLoading(false);
    }
  };

  if (!hotel) return <LoadingScreen />;

  const hotelName = hotel.name || "Ganesh Holiday Homes";
  const hotelPhone = hotel.phone || "+91 9673150591";
  const remainingAtCheckin = Math.max(0, totalAmount - bookingAmount);

  return (
    <div className="booking-page-container">
      <Navbar />

      {/* 1. Header Banner */}
      <div className="booking-header-banner">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <Link
                to="/"
                className="d-inline-flex align-items-center gap-2 text-white text-decoration-none mb-2 small fw-semibold"
              >
                <FaArrowLeft /> <span>Back to Home</span>
              </Link>
              <h1 className="booking-header-title">Book Your Stay</h1>
              <p className="booking-header-subtitle">
                Instant Availability • Secure UPI Token Advance • Fast Confirmation
              </p>
            </div>

            <div className="d-flex align-items-center gap-3">
              {isAiEnabled && (
                <div className="mode-switch-pill">
                  <button
                    type="button"
                    onClick={() => setUseVoiceMode(false)}
                    className={`mode-btn ${!useVoiceMode ? "active" : ""}`}
                  >
                    ✍️ Form Mode
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseVoiceMode(true)}
                    className={`mode-btn ${useVoiceMode ? "active" : ""}`}
                  >
                    🔊 Voice AI
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 2. Main Content Area */}
      <div className="container booking-main-wrapper">
        {/* Backend Offline / Maintenance Alert Banner */}
        {isBackendOffline && (
          <div
            className="p-3 mb-4 rounded-4 shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-3"
            style={{
              background: "#fffbeb",
              border: "1.5px solid #fde68a",
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <span className="fs-3">🛠️</span>
              <div>
                <strong className="d-block text-dark">Scheduled Maintenance in Progress</strong>
                <span className="small text-secondary">
                  Our online booking server is currently updating. You can still confirm your room instantly by phone or WhatsApp!
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMaintenanceModal(true)}
              className="btn btn-sm btn-dark rounded-pill px-3 py-2 fw-semibold shadow-sm flex-shrink-0"
            >
              Reserve via WhatsApp / Call
            </button>
          </div>
        )}

        {useVoiceMode ? (
          <div className="booking-step-card">
            <VoiceChat />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Left Column: Form Steps */}
              <div className="col-12 col-lg-7">
                {/* STEP 1: Stay & Room Selection */}
                <div className="booking-step-card">
                  <div className="step-card-header">
                    <div className="step-number-badge">1</div>
                    <div>
                      <h2 className="step-card-title">Stay & Room Details</h2>
                      <small className="text-muted">Choose your dates, room type, and occupancy</small>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="row g-3 mb-4">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-secondary">
                        <FaCalendarAlt className="text-danger me-1" /> Check-In Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-lg fs-6"
                        value={checkinDate}
                        min={todayStr}
                        onChange={(e) => {
                          setCheckinDate(e.target.value);
                          if (e.target.value >= checkoutDate) {
                            const next = new Date(e.target.value);
                            next.setDate(next.getDate() + 1);
                            setCheckoutDate(next.toISOString().split("T")[0]);
                          }
                        }}
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small text-secondary">
                        <FaCalendarAlt className="text-danger me-1" /> Check-Out Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-lg fs-6"
                        value={checkoutDate}
                        min={checkinDate || todayStr}
                        onChange={(e) => setCheckoutDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Live Date Availability Banner */}
                  {availableCount !== null && (
                    <div className="mb-4">
                      <div
                        className={`p-3 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2 small ${availableCount === 0
                          ? "bg-danger-subtle text-danger-emphasis border-danger-subtle"
                          : availableCount <= 3
                            ? "bg-warning-subtle text-dark border-warning-subtle shadow-sm"
                            : "bg-success-subtle text-success-emphasis border-success-subtle"
                          }`}
                      >
                        <div className="d-flex align-items-center gap-2 fw-semibold">
                          {isCheckingAvail ? (
                            <>
                              <FaSpinner className="spinner-border spinner-border-sm text-primary" />
                              <span>Checking live room availability...</span>
                            </>
                          ) : availableCount === 0 ? (
                            <>
                              <span className="fs-5">⚠️</span>
                              <span>
                                Fully Booked for {checkinDate} to {checkoutDate}. Please choose alternate dates.
                              </span>
                            </>
                          ) : availableCount === 1 ? (
                            <>
                              <span className="fs-5">🔥</span>
                              <span>
                                <strong>Hurry! Only 1 Room Available</strong> for your stay ({checkinDate} → {checkoutDate}) — Book now before it sells out!
                              </span>
                            </>
                          ) : availableCount <= 3 ? (
                            <>
                              <span className="fs-5">⚡</span>
                              <span>
                                <strong>High Demand: Only {availableCount} Rooms Available</strong> for your stay ({checkinDate} → {checkoutDate})!
                              </span>
                            </>
                          ) : (
                            <>
                              <FaCheckCircle className="text-success" />
                              <span>
                                Rooms Available for your stay ({checkinDate} → {checkoutDate})
                              </span>
                            </>
                          )}
                        </div>
                        {availableCount > 0 && availableCount <= 3 && (
                          <span className={`badge ${availableCount === 1 ? "bg-danger text-white" : "bg-warning text-dark"} fw-bold px-3 py-2 fs-6 shadow-sm`}>
                            ⚡ Only {availableCount} {availableCount === 1 ? "Room" : "Rooms"} Left!
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Room Type Selector (Visual Radio Cards) */}
                  <label className="form-label fw-semibold small text-secondary mb-2">
                    <FaBed className="text-danger me-1" /> Select Room Type
                  </label>
                  <div className="room-selection-grid">
                    {roomTypes.map((type) => {
                      const detail = ROOM_DETAILS[type] || getRoomCardDetails(type);
                      const dynamicPrice = getRoomPrice(type);
                      const isSelected = isRoomTypeMatch(selectedRoomType, type);
                      const typeLimits = getRoomLimits(configData, type);
                      const isThisDouble = isDoubleRoomType(type);
                      const roomTypeAvailCount = rawAvailData
                        ? isThisDouble
                          ? !rawAvailData.isDoublePairBooked &&
                            Array.isArray(rawAvailData.availableRooms) &&
                            rawAvailData.availableRooms.includes(4) &&
                            rawAvailData.availableRooms.includes(5)
                            ? 1
                            : 0
                          : (rawAvailData.availableRooms || []).length
                        : null;
                      const isCardAvailable = roomTypeAvailCount === null || roomTypeAvailCount > 0;

                      return (
                        <div
                          key={type}
                          className={`room-radio-card ${isSelected ? "selected" : ""} ${!isCardAvailable ? "opacity-75" : ""
                            }`}
                          onClick={() => setSelectedRoomType(type)}
                        >
                          <input
                            type="radio"
                            name="roomTypeSelect"
                            checked={isSelected}
                            onChange={() => setSelectedRoomType(type)}
                          />
                          <div className="room-radio-title">
                            <div className="d-flex align-items-center flex-wrap gap-2">
                              <span>{type}</span>
                              {rawAvailData && (
                                <span
                                  className={`badge ${!isCardAvailable
                                      ? "bg-danger text-white"
                                      : roomTypeAvailCount === 1
                                        ? "bg-danger-subtle text-danger border border-danger-subtle fw-bold"
                                        : roomTypeAvailCount <= 3
                                          ? "bg-warning-subtle text-dark border border-warning-subtle fw-bold"
                                          : "bg-success-subtle text-success border border-success-subtle"
                                    }`}
                                  style={{ fontSize: "0.72rem", padding: "3px 8px" }}
                                >
                                  {!isCardAvailable
                                    ? "Sold Out"
                                    : roomTypeAvailCount === 1
                                      ? "🔥 Only 1 Left"
                                      : roomTypeAvailCount <= 3
                                        ? `⚡ Only ${roomTypeAvailCount} Left`
                                        : "● Available"}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="room-radio-price">
                            ₹{dynamicPrice.toLocaleString("en-IN")}{" "}
                            <span className="small text-muted fw-normal">/ night</span>
                          </div>
                          <div className="room-radio-meta">
                            👥 Base {typeLimits.noCostLimit} (Max {typeLimits.maxLimit}) • 🛏️ {detail.bed}
                          </div>
                        </div>
                      );
                    })}
                  </div>


                  {/* Rooms & Guests */}
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold small text-secondary">
                        Number of Rooms
                      </label>
                      <select
                        className={`form-select form-select-lg fs-6 ${maxSelectableRooms === 0 ? "is-invalid border-danger" : ""
                          }`}
                        value={roomCount}
                        onChange={(e) => setRoomCount(parseInt(e.target.value) || 0)}
                        required
                        disabled={maxSelectableRooms === 0}
                      >
                        {maxSelectableRooms === 0 ? (
                          <option value={0}>0 Rooms (Fully Booked)</option>
                        ) : (
                          Array.from({ length: maxSelectableRooms }, (_, i) => i + 1).map((num) => (
                            <option key={num} value={num}>
                              {num} Room{num > 1 ? "s" : ""}
                            </option>
                          ))
                        )}
                      </select>
                      {maxSelectableRooms === 1 && isDoubleRoomType(selectedRoomType) && (
                        <div className="small text-muted mt-1 fw-semibold">
                          ℹ️ Max 1 Double Room available at property.
                        </div>
                      )}
                      {maxSelectableRooms === 0 && (
                        <div className="invalid-feedback d-block small mt-1 fw-semibold">
                          ⚠️ Fully booked for selected dates. Please choose alternate dates.
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold small text-secondary">
                        Adults / Guests (10+ yrs)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-lg fs-6"
                        id="people"
                        min="1"
                        max={maxAllowedGuests}
                        value={formData.people}
                        onChange={handleChange}
                        required
                      />
                      {formData.people > maxAllowedGuests ? (
                        <div className="text-danger small mt-1 fw-bold">
                          ⚠️ Exceeds max capacity of {maxAllowedGuests} guests for {roomCount} {selectedRoomType} ({roomLimits.maxLimit} guests/room).
                        </div>
                      ) : formData.people > noCostCapacity ? (
                        <div className="text-primary small mt-1">
                          ℹ️ Base rate covers {noCostCapacity} guests. +₹{resolvePerPersonPrice(configData?.per_person_price_map, selectedRoomType, 200)}/night per extra guest added ({formData.people - noCostCapacity} extra).
                        </div>
                      ) : (
                        <div className="text-muted small mt-1">
                          Base rate includes up to {noCostCapacity} guests (Max capacity: {maxAllowedGuests}).
                        </div>
                      )}
                    </div>

                    <div className="col-12 col-md-4">
                      <label className="form-label fw-semibold small text-secondary d-flex align-items-center justify-content-between">
                        <span>Children (&lt; 10 yrs)</span>
                        <span className="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-0.5 rounded-pill" style={{ fontSize: "0.72rem" }}>
                          FREE STAY
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-lg fs-6"
                        id="child"
                        min="0"
                        max="10"
                        placeholder="0"
                        value={formData.child}
                        onChange={handleChange}
                      />
                      <div className="text-muted small mt-1">
                        👶 Children below 10 years stay complimentary (free).
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 2: Guest Details */}
                <div className="booking-step-card">
                  <div className="step-card-header">
                    <div className="step-number-badge">2</div>
                    <div>
                      <h2 className="step-card-title">Guest Information</h2>
                      <small className="text-muted">Booking confirmation & invoice will be sent here</small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label htmlFor="fullName" className="form-label fw-semibold small text-secondary">
                        <FaUser className="text-danger me-1" /> Full Name
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-lg fs-6"
                        id="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="e.g. Tejas Patil"
                        required
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="phone" className="form-label fw-semibold small text-secondary">
                        <FaPhoneAlt className="text-danger me-1" /> Mobile Number
                      </label>
                      <input
                        type="tel"
                        className="form-control form-control-lg fs-6"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        placeholder="10-digit mobile number"
                        required
                        pattern="[6-9]{1}[0-9]{9}"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="email" className="form-label fw-semibold small text-secondary">
                        <FaEnvelope className="text-danger me-1" /> Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-lg fs-6"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="yourname@gmail.com"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* PROMO / COUPON CODE SECTION */}
                <div className="coupon-card mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-bold text-dark small d-flex align-items-center gap-2">
                      <FaTag className="text-danger" /> Have a Coupon / Promo Code?
                    </span>
                    {appliedCoupon && (
                      <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-1 small">
                        <FaCheckCircle className="me-1" /> Applied
                      </span>
                    )}
                  </div>

                  {!appliedCoupon ? (
                    <div>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control form-control-md text-uppercase fw-bold letter-spacing-1"
                          placeholder="ENTER COUPON CODE"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase());
                            if (couponError) setCouponError("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleApplyCoupon();
                            }
                          }}
                          disabled={isApplyingCoupon}
                        />
                        <button
                          type="button"
                          className="btn btn-dark fw-bold px-4"
                          onClick={handleApplyCoupon}
                          disabled={isApplyingCoupon || !couponCode.trim()}
                        >
                          {isApplyingCoupon ? (
                            <>
                              <FaSpinner className="spinner-border spinner-border-sm me-1" />
                              Applying...
                            </>
                          ) : (
                            "Apply"
                          )}
                        </button>
                      </div>

                      {couponError && (
                        <div className="text-danger small mt-2 d-flex align-items-center gap-1">
                          <FaExclamationTriangle /> {couponError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="applied-coupon-box p-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="bg-success text-white rounded-circle p-2 d-flex align-items-center justify-content-center"
                          style={{ width: 32, height: 32 }}
                        >
                          <FaCheckCircle />
                        </div>
                        <div>
                          <div className="fw-bold text-dark">{appliedCoupon.code}</div>
                          <small className="text-success fw-semibold">
                            ₹{appliedCoupon.discountAmount.toLocaleString("en-IN")} discount applied to total!
                          </small>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-pill px-3 py-1 fw-bold"
                        onClick={handleRemoveCoupon}
                      >
                        <FaTimes className="me-1" /> Remove
                      </button>
                    </div>
                  )}

                  {couponSuccess && !appliedCoupon && (
                    <div className="text-success small mt-2 d-flex align-items-center gap-1">
                      <FaCheckCircle /> {couponSuccess}
                    </div>
                  )}
                </div>

                {/* STEP 3: Advance Deposit & UPI Payment */}
                <div className="booking-step-card">
                  <div className="step-card-header">
                    <div className="step-number-badge">3</div>
                    <div>
                      <h2 className="step-card-title">Room Accommodation Advance Token (₹{bookingAmount})</h2>
                      <small className="text-muted">
                        Pay ₹{bookingAmount} advance token now to hold room; balance of ₹{remainingAtCheckin.toLocaleString("en-IN")} payable at check-in
                      </small>
                    </div>
                  </div>

                  {/* Payment Purpose Clarification Box */}
                  <div className="p-3 mb-3 rounded-3 bg-light border small text-secondary">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Payment Purpose:</span>
                      <strong className="text-dark">Short-Term Accommodation Advance Token</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span>Room Reserved:</span>
                      <strong className="text-dark">{selectedRoomType} ({roomCount} Room{roomCount > 1 ? "s" : ""}, {dayCount} Night{dayCount > 1 ? "s" : ""})</strong>
                    </div>
                    <div className="d-flex justify-content-between pt-1 border-top">
                      <span>Balance Payable at Check-in:</span>
                      <strong className="text-danger fw-bold">₹{remainingAtCheckin.toLocaleString("en-IN")}</strong>
                    </div>
                    <div className="text-muted mt-2 pt-1 border-top" style={{ fontSize: "0.8rem" }}>
                      ℹ️ This payment is an advance deposit toward your room stay at Ganesh Holiday Homes. Governed by our <Link to="/cancellation-refund-policy" className="text-danger fw-semibold" target="_blank">Cancellation & Refund Policy</Link>.
                    </div>
                  </div>

                  {/* Online Payment via PayU Card */}
                  <div
                    className="p-4 rounded-4 mb-3 border shadow-sm text-dark"
                    style={{
                      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                      borderColor: "#cbd5e1",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="p-2 rounded-3 bg-success bg-opacity-10 text-success fs-5">
                          <FaCreditCard />
                        </span>
                        <div>
                          <strong className="d-block text-dark fs-6">Pay Online via PayU Gateway</strong>
                          <span className="text-muted small">UPI, GPay, PhonePe, Paytm, Debit/Credit Cards & NetBanking</span>
                        </div>
                      </div>
                      <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-1 rounded-pill small fw-semibold">
                        <FaLock className="me-1" /> 256-Bit SSL Encrypted
                      </span>
                    </div>

                    <div className="p-3 bg-white rounded-3 border mb-3">
                      <div className="d-flex align-items-center justify-content-between text-secondary small mb-1">
                        <span>Advance Payable Now:</span>
                        <strong className="text-success fs-5">₹{bookingAmount.toLocaleString("en-IN")}</strong>
                      </div>
                      <div className="d-flex align-items-center justify-content-between text-secondary small">
                        <span>Balance at Check-in:</span>
                        <strong className="text-danger">₹{remainingAtCheckin.toLocaleString("en-IN")}</strong>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 text-secondary small flex-wrap">
                      <span className="d-inline-flex align-items-center gap-1">
                        <FaCheckCircle className="text-success" /> Instant Confirmation
                      </span>
                      <span className="d-inline-flex align-items-center gap-1">
                        <FaCheckCircle className="text-success" /> Zero Extra Gateway Fees
                      </span>
                      <span className="d-inline-flex align-items-center gap-1">
                        <FaCheckCircle className="text-success" /> Official Invoice on Payment
                      </span>
                    </div>
                  </div>

                  {/* Availability / Validation Error Banner */}
                  {availError && (
                    <div
                      className="alert alert-danger d-flex align-items-center gap-3 rounded-3 mt-3 shadow-sm border-danger"
                      role="alert"
                    >
                      <div className="fs-4 text-danger">⚠️</div>
                      <div>
                        <strong className="d-block">Booking Notice</strong>
                        <span className="small">{availError}</span>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="mt-4">
                    <button
                      type="submit"
                      className="btn-luxury-primary w-100 py-3 d-flex align-items-center justify-content-center gap-2"
                      style={{ fontSize: "clamp(0.95rem, 3.8vw, 1.12rem)", letterSpacing: "0.2px" }}
                      disabled={isLoading || maxSelectableRooms === 0}
                    >
                      {isLoading ? (
                        <>
                          <FaSpinner className="fa-spin" />
                          <span>Connecting to PayU Gateway...</span>
                        </>
                      ) : maxSelectableRooms === 0 ? (
                        <>
                          <span>⚠️ Sold Out for Selected Dates</span>
                        </>
                      ) : (
                        <>
                          <FaLock className="flex-shrink-0" />
                          <span>Pay ₹{Number(bookingAmount).toLocaleString("en-IN")} Advance • Instant Lock</span>
                        </>
                      )}
                    </button>
                    <p className="text-center text-muted small mt-2 mb-0">
                      🔒 Secured by PayU Payments. You will be redirected to complete your payment.
                    </p>
                  </div>

                </div>
              </div>

              {/* Right Column: Sticky Live Booking Summary */}
              <div className="col-12 col-lg-5">
                <div className="sticky-summary-card">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="badge-luxury py-1 px-3 rounded-pill small">
                      <FaStar className="text-warning me-1" /> 4.8 Top Rated
                    </span>
                    <span className="text-muted small">
                      <FaMapMarkerAlt className="text-danger me-1" /> Shrivardhan
                    </span>
                  </div>

                  <h3 className="summary-hotel-name">{hotelName}</h3>
                  <p className="text-muted small mb-0">5-Min Beach Walk • Homely Konkani Meals</p>

                  {/* Dates Box */}
                  <div className="summary-dates-box">
                    <div className="row g-2 text-center align-items-center">
                      <div className="col-5 summary-date-col">
                        <span className="summary-date-label">Check-In</span>
                        <span className="summary-date-val">{checkinDate}</span>
                      </div>
                      <div className="col-2 text-center">
                        <span className="summary-nights-badge">{dayCount}N</span>
                      </div>
                      <div className="col-5 summary-date-col">
                        <span className="summary-date-label">Check-Out</span>
                        <span className="summary-date-val">{checkoutDate}</span>
                      </div>
                    </div>
                  </div>

                  {/* Live Availability Verification Pill */}
                  {availableCount !== null && (
                    <div className="mt-2 mb-3">
                      <div
                        className={`p-2 rounded-3 text-center small fw-semibold ${availableCount === 0
                          ? "bg-danger-subtle text-danger border border-danger-subtle"
                          : availableCount === 1
                            ? "bg-danger-subtle text-danger border border-danger-subtle fw-bold"
                            : availableCount <= 3
                              ? "bg-warning-subtle text-dark border border-warning-subtle fw-bold"
                              : "bg-success-subtle text-success border border-success-subtle"
                          }`}
                      >
                        {availableCount === 0
                          ? "⚠️ Sold Out for Selected Dates"
                          : availableCount === 1
                            ? "🔥 Hurry! Only 1 Room Left for Selected Dates — Lock Your Stay Now!"
                            : availableCount <= 3
                              ? `⚡ High Demand: Only ${availableCount} Rooms Available for Selected Dates!`
                              : "✓ Live Verified: Rooms Available for Your Stay"}
                      </div>
                    </div>
                  )}

                  {/* Selection Summary */}
                  <div className="mb-3">
                    <div className="summary-breakdown-row">
                      <span>Room Category</span>
                      <strong className="text-dark">{selectedRoomType}</strong>
                    </div>
                    <div className="summary-breakdown-row">
                      <span>Base Nightly Rate</span>
                      <span>₹{bookingCalc.baseRate.toLocaleString("en-IN")} / room</span>
                    </div>
                    {bookingCalc.extraGuests > 0 && (
                      <div className="summary-breakdown-row text-danger">
                        <span>
                          Extra Guests ({bookingCalc.extraGuests} extra × ₹{bookingCalc.perPersonExtraRate})
                        </span>
                        <span>+₹{bookingCalc.extraCostPerNight.toLocaleString("en-IN")} / night</span>
                      </div>
                    )}
                    <div className="summary-breakdown-row">
                      <span>Rooms & Guests</span>
                      <span>
                        {roomCount} Room{roomCount > 1 ? "s" : ""} • {formData.people} Adults
                        {Number(formData.child) > 0 ? `, ${formData.child} Child${Number(formData.child) > 1 ? "ren" : ""}` : ""}
                      </span>
                    </div>
                    <div className="summary-breakdown-row">
                      <span>Duration</span>
                      <span>{dayCount} Night{dayCount > 1 ? "s" : ""}</span>
                    </div>

                    {appliedCoupon && appliedCoupon.discountAmount > 0 && (
                      <>
                        <div className="summary-breakdown-row text-muted small pt-2 border-top">
                          <span>Subtotal</span>
                          <span>₹{bookingCalc.totalAmount.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="summary-breakdown-row text-success fw-bold">
                          <span className="d-flex align-items-center gap-1">
                            <FaTag /> Coupon Discount ({appliedCoupon.code})
                          </span>
                          <span>-₹{appliedCoupon.discountAmount.toLocaleString("en-IN")}</span>
                        </div>
                      </>
                    )}

                    <div className="summary-breakdown-row total-row">
                      <span>Total Estimated Bill</span>
                      <span className="text-danger">₹{Number(totalAmount).toLocaleString("en-IN")}</span>
                    </div>
                  </div>


                  {/* Token Advance Callout */}
                  <div className="advance-token-box">
                    <div className="small text-muted mb-1">Advance Deposit Due Now</div>
                    <div className="advance-token-amount">₹{bookingAmount.toLocaleString("en-IN")}</div>
                    <div className="advance-token-note">
                      Balance of ₹{remainingAtCheckin.toLocaleString("en-IN")} payable upon check-in.
                    </div>
                  </div>

                  {/* Trust Badges */}
                  <div className="trust-badges-list">
                    <div className="trust-badge-item">
                      <FaCheckCircle className="trust-badge-icon" />
                      <span>Instant Booking Request & Email Confirmation</span>
                    </div>
                    <div className="trust-badge-item">
                      <FaShieldAlt className="trust-badge-icon" />
                      <span>100% Verified Gated Beach Homestay</span>
                    </div>
                    <div className="trust-badge-item">
                      <FaPhoneAlt className="trust-badge-icon" />
                      <span>Need help? Call Host at <strong>{hotelPhone}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* Booking Success & Payment Verification Modal */}
        {bookingSuccessData && (
          <div className="booking-modal-overlay animate-fade-in">
            <div className="booking-success-modal shadow-2xl animate-scale-up">
              {/* Header */}
              <div className="modal-success-header text-center">
                <div className="success-icon-badge mx-auto mb-3">
                  <FaCheckCircle />
                </div>
                <h2 className="modal-title fw-bold text-dark mb-1 fs-4">
                  Booking Request Received!
                </h2>
                <p className="text-muted small mb-0">
                  Payment Verification in Progress ⏳
                </p>
              </div>

              {/* Verification Alert Banner */}
              <div className="verification-notice-banner p-3 my-3 rounded-3">
                <div className="d-flex align-items-start gap-2">
                  <FaShieldAlt className="text-success mt-1 flex-shrink-0" />
                  <div className="small text-dark">
                    <strong>Thank you, {bookingSuccessData.name}!</strong> We have received your booking details & token advance submission. Our front desk team is verifying the payment and will send your official confirmation via <strong>WhatsApp & Email</strong> shortly.
                  </div>
                </div>
              </div>

              {/* Booking Details Card */}
              <div className="modal-details-card p-3 rounded-3 mb-3">
                <div className="row g-2 small">
                  <div className="col-6">
                    <span className="text-muted d-block">Room Type</span>
                    <strong className="text-dark">{bookingSuccessData.roomType}</strong>
                  </div>
                  <div className="col-6 text-end">
                    <span className="text-muted d-block">Rooms & Guests</span>
                    <strong className="text-dark">
                      {bookingSuccessData.roomCount} Room{bookingSuccessData.roomCount > 1 ? "s" : ""} • {bookingSuccessData.people} Adults
                      {Number(bookingSuccessData.child) > 0 ? `, ${bookingSuccessData.child} Child${Number(bookingSuccessData.child) > 1 ? "ren" : ""}` : ""}
                    </strong>
                  </div>
                  <div className="col-6 mt-2">
                    <span className="text-muted d-block">Stay Dates</span>
                    <strong className="text-dark">
                      {bookingSuccessData.checkinDateFormatted} → {bookingSuccessData.checkoutDateFormatted} ({bookingSuccessData.nights}N)
                    </strong>
                  </div>
                  <div className="col-6 mt-2 text-end">
                    <span className="text-muted d-block">Advance Token</span>
                    <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 fw-bold">
                      ₹{Number(bookingSuccessData.bookingAmount).toLocaleString("en-IN")} Received
                    </span>
                  </div>
                  {bookingSuccessData.discountAmount > 0 && (
                    <div className="col-12 mt-2 pt-1 border-top d-flex justify-content-between text-success">
                      <span>Coupon Discount ({bookingSuccessData.couponCode})</span>
                      <strong>-₹{Number(bookingSuccessData.discountAmount).toLocaleString("en-IN")}</strong>
                    </div>
                  )}
                  <div className="col-6 mt-2 pt-1 border-top">
                    <span className="text-muted d-block">Total Estimated Bill</span>
                    <strong className="text-dark fs-6">₹{Number(bookingSuccessData.totalAmount).toLocaleString("en-IN")}</strong>
                  </div>
                  <div className="col-6 mt-2 pt-1 border-top text-end">
                    <span className="text-muted d-block">Balance at Check-in</span>
                    <strong className="text-danger fs-6">₹{Number(bookingSuccessData.remainingAmount).toLocaleString("en-IN")}</strong>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex flex-column gap-2 mt-4">
                {/* View Official Receipt */}
                <Link
                  to="/booking-confirmation"
                  state={{
                    bookingId: bookingSuccessData.bookingId || `GHH-${Math.floor(100000 + Math.random() * 900000)}`,
                    name: bookingSuccessData.name,
                    roomType: bookingSuccessData.roomType,
                    roomCount: bookingSuccessData.roomCount,
                    people: bookingSuccessData.people,
                    checkinDate: bookingSuccessData.checkinDateFormatted,
                    checkoutDate: bookingSuccessData.checkoutDateFormatted,
                    bookingAmount: bookingSuccessData.bookingAmount,
                    totalAmount: bookingSuccessData.totalAmount,
                  }}
                  className="btn btn-outline-primary py-2 fw-bold rounded-pill shadow-sm d-flex align-items-center justify-content-center gap-2"
                >
                  <FaCheckCircle /> View / Print Confirmation Receipt
                </Link>

                {/* WhatsApp Host Button */}
                <a
                  href={`https://wa.me/91${(hotel?.phone || "9422090466").replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(
                    `Hello Ganesh Holiday Homes! I have submitted a booking for ${bookingSuccessData.roomCount} ${bookingSuccessData.roomType} (${bookingSuccessData.checkinDateFormatted} to ${bookingSuccessData.checkoutDateFormatted}) for ${bookingSuccessData.name}. Advance amount: Rs. ${bookingSuccessData.bookingAmount}. Please verify and confirm.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-success py-2 fw-bold rounded-pill shadow-sm d-flex align-items-center justify-content-center gap-2"
                >
                  <FaWhatsapp className="fs-5" /> Chat with Host on WhatsApp
                </a>

                <div className="d-flex gap-2">
                  <Link
                    to="/"
                    className="btn btn-outline-dark flex-fill py-2 fw-bold rounded-pill"
                  >
                    Return to Home
                  </Link>
                  <button
                    type="button"
                    onClick={() => setBookingSuccessData(null)}
                    className="btn btn-danger flex-fill py-2 fw-bold rounded-pill shadow-sm"
                  >
                    Book Another Room
                  </button>
                </div>
              </div>

              {/* Host Contact Helper */}
              <p className="text-center text-muted small mt-3 mb-0">
                Direct Help: <a href={`tel:${hotel?.phone || "+919422090466"}`} className="text-decoration-none fw-bold text-dark">{hotel?.phone || "+91 9422090466"}</a>
              </p>
            </div>
          </div>
        )}

        {/* System Maintenance / Offline Graceful Modal */}
        <MaintenanceModal
          isOpen={showMaintenanceModal}
          onClose={() => setShowMaintenanceModal(false)}
          onRetry={() => {
            setShowMaintenanceModal(false);
          }}
          hotelPhone={hotelPhone}
          bookingDetails={{
            name: formData.fullName,
            checkinDate,
            checkoutDate,
            roomType: selectedRoomType,
            people: formData.people,
            roomCount,
          }}
        />
      </div>

      <Footer />
    </div>
  );
};

export default BookingForm;
